// ChartLayerManager - unified chart tab management using e80c574d architecture
// Uses UnifiedChartTab components with proven rendering system
// Архитектура: Unified chart+details components with event-driven data flow

import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { ChartInstance } from '../../../types/MultipleCharts';
import { SharedChartData, SharedViewMode } from '../../../types/share.types';
import UnifiedChartTab, { UnifiedChartTabRef } from './UnifiedChartTab';
import './ChartLayerStyles.css';

/**
 * Пропы для ChartLayerManager
 */
export interface ChartLayerManagerProps {
  charts: Map<string, ChartInstance>;
  activeChartId: string | null;
  loadedChartIds: string[];
  showDetails?: boolean;
  detailsPosition?: 'right' | 'bottom';
  preloadNext?: boolean;
  debugMode?: boolean;
  className?: string;
  shareMode?: boolean;  // If true, pass to UnifiedChartTab (all sections always visible)
  sharedViewMode?: SharedViewMode;  // View mode for shared charts (author/guest)
  sharedChartData?: SharedChartData;  // Shared chart data with share_config
}

/**
 * ChartLayerManager - главный менеджер слоёв чартов
 * 
 * Особенности:
 * - CSS-переключение вместо display: none/block
 * - Предзагрузка следующего чарта
 * - GPU-ускоренные переходы
 * - Управление состоянием слоёв
 * - Оптимизация памяти
 */
export const ChartLayerManager: React.FC<ChartLayerManagerProps> = ({
  charts,
  activeChartId,
  loadedChartIds,
  showDetails = true,
  detailsPosition = 'right',
  preloadNext = true,
  debugMode = false,
  className,
  shareMode = false,
  sharedViewMode = 'author',
  sharedChartData
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isWorkspaceRestoring, setIsWorkspaceRestoring] = useState(false);

  // NOTE: chartStates removed - status is derived from props, refs stored in chartRefsMap
  const chartRefsMap = useRef<Map<string, React.RefObject<UnifiedChartTabRef>>>(new Map());

  // Отслеживание изменений активного чарта для оптимизации
  const prevActiveId = useRef<string | null>(null);

  // CSS-только переключение активного чарта через классы + TabChartRenderer activation
  useEffect(() => {
    if (prevActiveId.current !== activeChartId) {

      // НОВАЯ СИСТЕМА: Уведомить TabChartRenderer об изменении активности
      const tabRenderers = (window as any).tabChartRenderers as Map<string, any> | undefined;

      if (tabRenderers) {
        // Деактивировать предыдущий активный таб
        if (prevActiveId.current && tabRenderers.has(prevActiveId.current)) {
          const prevRenderer = tabRenderers.get(prevActiveId.current);
          if (prevRenderer?.setTabActive) {
            prevRenderer.setTabActive(false);
          }
        }

        if (activeChartId && tabRenderers.has(activeChartId)) {
          const activeRenderer = tabRenderers.get(activeChartId);
          if (activeRenderer?.setTabActive) {
            activeRenderer.setTabActive(true);
          }
        }
      }

      // Убираем класс active у всех чартов
      if (containerRef.current) {
        const allChartElements = containerRef.current.querySelectorAll('.unified-chart-renderer');
        allChartElements.forEach(element => {
          element.classList.remove('active');
        });
      }

      // Добавляем класс active только активному чарту
      if (activeChartId) {
        const activeElement = containerRef.current?.querySelector(`.chart-layer-${activeChartId}`) as HTMLElement;
        if (activeElement) {
          activeElement.classList.add('active');
        } else {
        }
      }

      prevActiveId.current = activeChartId;
    }
  }, [activeChartId]);
  
  // Отслеживание состояния восстановления workspace
  useEffect(() => {
    const handleWorkspaceRestore = () => {
      setIsWorkspaceRestoring(true);
      
      // Отключаем анимации на время восстановления
      setTimeout(() => {
        setIsWorkspaceRestoring(false);
      }, 1000);
    };
    
    // Слушаем события восстановления workspace
    if (typeof window !== 'undefined') {
      window.addEventListener('workspace-restore-start', handleWorkspaceRestore);
      
      return () => {
        window.removeEventListener('workspace-restore-start', handleWorkspaceRestore);
      };
    }
  }, []);
  
  // Инициализация видимости через CSS классы после рендеринга новых чартов
  useEffect(() => {
    if (activeChartId) {
      // Небольшая задержка чтобы DOM обновился
      setTimeout(() => {
        const activeElement = containerRef.current?.querySelector(`.chart-layer-${activeChartId}`) as HTMLElement;
        if (activeElement) {
          activeElement.classList.add('active');
        } else {
        }
      }, 0);
    }
  }, [loadedChartIds, activeChartId]);
  
  
  // API функции экспортируются через пропы или контекст
  
  // Контейнер стили
  const containerClasses = [
    'chart-layer-container',
    debugMode ? 'debug-mode' : '',
    isWorkspaceRestoring ? 'workspace-restoring' : '',
    className || ''
  ].filter(Boolean).join(' ');
  
  return (
    <Box
      ref={containerRef}
      className={containerClasses}
      width="100%"
      height="100%"
      position="relative"
      overflow="hidden"
    >
      {/* Render charts directly - memo will handle re-render prevention */}
      {loadedChartIds.map(chartId => {
        const chart = charts.get(chartId);

        if (!chart || !chart.chartData) {
          return (
            <Box
              key={`loading-${chartId}`}
              className={`chart-loading-placeholder chart-layer-${chartId}`}
              width="100%"
              height="100%"
              bg="gray.100"
              color="gray.500"
              fontSize="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              Loading chart {chartId.substring(0, 8)}...
            </Box>
          );
        }

        return (
          <UnifiedChartTab
            key={chartId}
            chartId={chartId}
            chartData={chart.chartData}
            isActive={false}
            displayMode="short"
            enableAnimations={!isWorkspaceRestoring}
            showDetails={showDetails}
            className={`unified-chart-renderer chart-layer-${chartId}`}
            debugMode={debugMode}
            width="100%"
            height="100%"
            shareMode={shareMode}
            sharedViewMode={sharedViewMode}
            sharedChartData={sharedChartData}
            chartSettings={chart.settings}
          />
        );
      })}

      {/* Debug информация */}
      {debugMode && (
        <Box
          position="absolute"
          top={2}
          right={2}
          background="rgba(0, 0, 0, 0.8)"
          color="white"
          padding={2}
          borderRadius="md"
          fontSize="xs"
          zIndex={1000}
          pointerEvents="none"
        >
          <div>Charts: {loadedChartIds.length}</div>
          <div>Active: {activeChartId || 'none'}</div>
          <div>Restoring: {isWorkspaceRestoring ? 'yes' : 'no'}</div>
        </Box>
      )}
    </Box>
  );
};

// Memoize with custom comparator - only re-render when chart data actually changes
export default React.memo(ChartLayerManager, (prevProps, nextProps) => {
  // Always re-render if these change
  if (prevProps.activeChartId !== nextProps.activeChartId) return false;
  if (prevProps.showDetails !== nextProps.showDetails) return false;
  if (prevProps.shareMode !== nextProps.shareMode) return false;
  if (prevProps.sharedViewMode !== nextProps.sharedViewMode) return false;

  // Compare loadedChartIds arrays
  if (prevProps.loadedChartIds.length !== nextProps.loadedChartIds.length) return false;
  if (!prevProps.loadedChartIds.every((id, i) => id === nextProps.loadedChartIds[i])) return false;

  // Compare chart data and settings for each loaded chart
  for (const chartId of nextProps.loadedChartIds) {
    const prevChart = prevProps.charts.get(chartId);
    const nextChart = nextProps.charts.get(chartId);

    // If chart exists in one but not other, re-render
    if (!prevChart || !nextChart) return false;

    // Only re-render if chartData or settings reference changed
    if (prevChart.chartData !== nextChart.chartData) return false;
    if (prevChart.settings !== nextChart.settings) return false;
  }

  // Skip re-render - nothing important changed
  return true;
});

// NOTE: shouldPreload function removed - preloading now handled via CSS visibility